import { Volume2, Play, Pause, Download, RefreshCw, Mic2, BookOpen, FileText, Wand2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useAuth } from '../services/auth'
import { useCredits } from '../components/hooks/credits'
import { CreditWarning } from '../components/dashboard/creditstatus'

const AVAILABLE_VOICES = [
  { value: 'af_bella', label: 'Bella' },
  { value: 'af_sky', label: 'Sky' },
  { value: 'af_bella+af_sky', label: 'Bella + Sky' }
];

const TextToSpeech = () => {
  const { processCredits, refundCredits, hasEnoughCredits } = useCredits()
  const { refreshJWT, isAuthenticated } = useAuth()
  
  const [text, setText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [speed, setSpeed] = useState(1);
  const [audioUrl, setAudioUrl] = useState("");
  const [error, setError] = useState("");
  const [useFillers, setUseFillers] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const backendUrl = process.env.NODE_ENV === "development" 
    ? "http://localhost:3004" 
    : process.env.REACT_APP_BACKEND_URL;

  const handleGenerate = async () => {
    if (!text.trim() || !selectedVoice) {
      setError('Please enter text and select a voice');
      return;
    }

    if (!isAuthenticated) {
      setError('Please log in to generate speech');
      return;
    }

    if (!hasEnoughCredits()) {
      setError('You need at least 2 credits to generate speech');
      return;
    }

    try {
      const success = await processCredits();
      if (!success) {
        setError('Insufficient credits to generate speech');
        return;
      }

      setIsGenerating(true);
      setError('');

      const token = await refreshJWT();
      
      const response = await fetch(`${backendUrl}/api/tts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text,
          voice_name: selectedVoice,
          speed,
          useFillers
        }),
      });

      if (!response.ok) {
        await refundCredits(); // Refund credits on failure
        throw new Error('Generation failed');
      }

      const audioBlob = await response.blob();
      
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      
      const newAudioUrl = URL.createObjectURL(audioBlob);
      setAudioUrl(newAudioUrl);

    } catch (error) {
      await refundCredits(); // Refund credits on failure
      setError('Failed to generate speech. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClear = () => {
    setIsClearing(true);
    setTimeout(() => {
      setText("");
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl("");
      }
      setIsClearing(false);
    }, 1000);
  };

  const handleDownload = () => {
    if (!audioUrl) return;
    
    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = 'generated-speech.mp3';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Cleanup audio URL when component unmounts
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  return (
    <div className="p-8 lg:p-12 bg-[#f8fafc] dark:bg-[#0f172a] min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Original title styling */}
        <div className="mb-16 mt-8">
          <h1 className="text-6xl md:text-7xl font-medium text-gray-800 dark:text-gray-100 tracking-tight leading-[1.2] mb-4">
            Text to Speech<br />
            Generation
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Convert text into natural-sounding speech with advanced AI voices
          </p>
        </div>

        {/* Add Credit Warning */}
        <CreditWarning />

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="bg-white dark:bg-[#1e293b]/60 rounded-3xl p-8 lg:p-10 border-2 border-gray-200 dark:border-gray-700">
          <div className="mb-6">
            <label className="block text-base font-medium mb-2">Select Voice</label>
            <Select value={selectedVoice} onValueChange={setSelectedVoice}>
              <SelectTrigger className="w-full text-base py-5 px-4 rounded-xl border-2 border-gray-200 dark:border-gray-700">
                <SelectValue placeholder="Choose a voice" />
              </SelectTrigger>
              <SelectContent className="text-base">
                {AVAILABLE_VOICES.map((voice) => (
                  <SelectItem key={voice.value} value={voice.value} className="py-2.5">
                    {voice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-base font-medium">Add Natural Fillers</label>
              <Switch
                checked={useFillers}
                onCheckedChange={setUseFillers}
                className="bg-black dark:bg-white data-[state=checked]:bg-blue-500"
              />
            </div>
            {useFillers && (
              <p className="text-sm text-gray-500 mt-2">
                Will add natural filler words (um, uh, like) to make speech sound more conversational
              </p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-base font-medium mb-2">Speed</label>
            <div className="px-3">
              <Slider
                value={[speed]}
                onValueChange={([value]) => setSpeed(value)}
                min={0.5}
                max={2}
                step={0.1}
                className="w-full bg-black dark:bg-white"
              />
            </div>
            <div className="text-sm text-gray-500 mt-2">
              {speed.toFixed(1)}x
            </div>
          </div>

          <Textarea
            placeholder="Start typing here or paste any text you want to turn into lifelike speech..."
            className="min-h-[280px] text-lg leading-relaxed p-5 bg-transparent border-2 border-gray-200 dark:border-gray-700 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 font-medium"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

<div className="flex flex-col sm:flex-row justify-end mt-6 gap-4">
  <Button
    variant="outline"
    className="text-base py-3 sm:py-5 px-4 sm:px-6 rounded-xl w-full sm:w-auto"
    onClick={handleClear}
    disabled={isGenerating || isClearing}
  >
    {isClearing ? (
      <RefreshCw className="w-4 sm:w-5 h-4 sm:h-5 animate-spin" />
    ) : (
      "Clear"
    )}
  </Button>
  
  <Button
    className="bg-blue-500 hover:bg-blue-600 text-white text-base py-3 sm:py-5 px-6 sm:px-8 rounded-xl w-full sm:w-auto"
    onClick={handleGenerate}
    disabled={!text.trim() || !selectedVoice || isGenerating}
  >
    {isGenerating ? (
      <>
        <RefreshCw className="w-4 sm:w-5 h-4 sm:h-5 animate-spin mr-2" />
        Generating...
      </>
    ) : (
      <>
        <Play className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
        Generate Speech
      </>
    )}
  </Button>
</div>
        </div>

        {audioUrl && (
          <div className="bg-white dark:bg-[#1e293b]/60 rounded-3xl p-8 border-2 border-gray-200 dark:border-gray-700 mt-8">
            <h3 className="text-2xl font-medium text-gray-800 dark:text-gray-100 mb-6">
              Generated Audio
            </h3>
            
            <div className="space-y-4">
              <audio
                controls
                className="w-full"
                src={audioUrl}
              />
              <Button
                variant="outline"
                className="w-full text-base py-5 rounded-xl flex items-center justify-center gap-2"
                onClick={handleDownload}
              >
                <Download className="w-5 h-5" />
                Download Audio
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextToSpeech;